import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CommonModal from '../CommonModal';

/**
 * UnsavedChangesModal - Reusable confirmation modal for unsaved changes.
 *
 * "Discard"       — navigates away, losing changes  (onConfirm)
 * "Keep in Page"  — closes modal, stays on page     (onCancel)
 * X / close button — stays on page                   (onCancel)
 *
 * Backdrop clicks and Escape key are blocked.
 */
const UnsavedChangesModal = ({ showPrompt, onConfirm, onCancel }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!showPrompt) return;
    const block = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', block, true);
    return () => document.removeEventListener('keydown', block, true);
  }, [showPrompt]);

  return (
    <CommonModal
      open={showPrompt}
      variant="warning"
      title={t('Unsaved Changes')}
      message={t('You have unsaved changes. Are you sure you want to leave?')}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onCancel();
      }}
      buttonProps={[
        { label: t('Discard'), onClick: () => onConfirm(), status: 'danger' },
        { label: t('Keep in Page'), onClick: () => onCancel() },
      ]}
      id="unsaved-changes"
    />
  );
};

UnsavedChangesModal.propTypes = {
  showPrompt: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default UnsavedChangesModal;
